import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { runWalletAlerts } from "./engines/walletHealth/walletAlertsEngine";
import WalletAlertsPanel from "./components/WalletAlertsPanel";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";


import "./App.css";
import { auth, signOut } from "./firebase";

const TOKENS_PER_CHAIN = 7;   // per-chain tables + sparkline + CoinGecko
const AI_TOP_TOKENS = 6;      // AI recommendations
const PIE_TOP_TOKENS = 7;     // portfolio pie slices
const RISK_TABLE_TOKENS = 12;  // Token Risk Scanner - limited by RISK_TABLE_TOKENS
const AFFORDABLE_TOP_TOKENS = 10;

// Smooth scroll throttling helper
const throttle = (fn, delay) => {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn(...args);
    }
  };
};

/* ==========================================
   SPARKLINE COMPONENT (mini inline chart)
========================================== */
const Sparkline = ({ points }) => {
  if (!points || points.length === 0) return null;

  const width = 80;
  const height = 24;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const coords = points
    .map((value, index) => {
      const x = (index / (points.length - 1 || 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <polyline points={coords} />
    </svg>
  );
};

/* Fallback synthetic series based on base USD value */
const buildSyntheticSeries = (base) => {
  const b = base || 1;
  const multipliers = [0.96, 0.98, 1.0, 1.03, 1.01, 1.05, 1.04];
  return multipliers.map((m) => b * m);
};

function WalletTracer() {
  const [walletAddress, setWalletAddress] = useState("");
  const [inputError, setInputError] = useState("");
  const [resolvedENS, setResolvedENS] = useState(null); // holds "name.eth" if user enters ENS
  const [inputStatus, setInputStatus] = useState(""); // info message
  const [resolvingENS, setResolvingENS] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [scrolled, setScrolled] = useState(false); // Navbar shrink state
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [marketData, setMarketData] = useState({}); // CoinGecko price + MC
  const [cgHistory, setCgHistory] = useState({});   // chainId-address Prices
  const [cgChange30d, setCgChange30d] = useState({}); // chainId-address Numbers
  const [walletAlerts, setWalletAlerts] = useState([]);

  
  // Pro States
  const [isPro, setIsPro] = useState(false);
  const [proLoading, setProLoading] = useState(true);
 // Stripe Redirect State
  const [checkoutLoading, setCheckoutLoading] = useState(false);

const handlePdfClick = () => {
  if (!isPro) {
    setInputError("LIMIT_REACHED"); // 🔁 reuse existing upgrade UI
    return;
  }

  generatePDF(); // ✅ Pro users only
};

const handleUpgrade = async () => {
  if (checkoutLoading) return;

  // 🔴 CLOSE upgrade modal immediately
  setInputError("");

  // 🔵 SHOW checkout feedback
  setCheckoutLoading(true);

  try {
    const res = await fetch(
      "https://createcheckoutsession-6w66jvsixq-uc.a.run.app",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );

    const data = await res.json();

    if (!data.url) {
      throw new Error("No checkout URL returned");
    }

    // Let React paint the overlay before redirect
    requestAnimationFrame(() => {
      window.location.href = data.url;
    });

  } catch (err) {
    console.error("Checkout error:", err);
    alert("Failed to start checkout");
    setCheckoutLoading(false);
  }
};

  const generatePDF = () => {
  if (!walletData) return;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = 14;

  // ===== HEADER BAR
  doc.setFillColor(...PDF_THEME.bg);
  doc.rect(0, 0, 210, 28, "F");

  doc.setTextColor(...PDF_THEME.text);
  doc.setFontSize(16);
  doc.text("MultiVersal Wallet Report", 10, 17);

  doc.setFontSize(9);
  doc.setTextColor(...PDF_THEME.muted);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 23);

  y = 36;

  // ===== SUMMARY CARD
  doc.setFillColor(...PDF_THEME.card);
  doc.roundedRect(10, y, 190, 26, 3, 3, "F");

  doc.setTextColor(...PDF_THEME.text);
  doc.setFontSize(11);
  doc.text("Summary", 14, y + 7);

  doc.setFontSize(10);
  doc.text(`Total Value: $${formatUSD(getPortfolioTotal())}`, 14, y + 14);
  doc.text(`Wallet Health: ${walletHealth}/100`, 90, y + 14);
  doc.text(`Clutter Score: ${clutterScore}`, 150, y + 14);

  y += 36;

  // ===== PIE CHART
  const slices = getPortfolioSlices();
  if (slices.length > 0) {
    const pieImg = drawPieChartCanvas(slices, 180);
    doc.addImage(pieImg, "PNG", 10, y, 60, 60);

    doc.setFontSize(11);
    doc.text("Portfolio Distribution", 75, y + 6);

    doc.setFontSize(9);
    slices.forEach((s, i) => {
      if (i > 6) return;
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(...hexToRgb(s.color));
      doc.rect(75, y + 12 + i * 6, 4, 4, "F");
      doc.text(`${s.label} (${s.percent.toFixed(1)}%)`, 82, y + 16 + i * 6);
    });

    y += 70;
  }

  // ===== TOP ASSETS TABLE
  const topAssets = buildAllTokensFlat(walletData)
    .filter(t => t.valueUSD > 0)
    .sort((a, b) => b.valueUSD - a.valueUSD)
    .slice(0, 7)
    .map((t, i) => [
      i + 1,
      t.symbol || "—",
      t.name || "—",
      `$${formatUSD(t.valueUSD)}`
    ]);

  autoTable(doc, {
    startY: y,
    head: [["#", "Symbol", "Token", "Value (USD)"]],
    body: topAssets,
    theme: "grid",
    headStyles: {
      fillColor: PDF_THEME.bg,
      textColor: PDF_THEME.accent,
    },
    styles: {
      fontSize: 9,
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ===== PER-CHAIN TABLES
  walletData.forEach((chain) => {
    if (y > 250) {
      doc.addPage();
      y = 14;
    }

    doc.setFontSize(11);
    doc.text(
      `${chain.chain} — Total: $${formatUSD(getChainTotal(chain.items))}`,
      10,
      y
    );

    const rows = chain.items
      .filter(t => t.balance > 0)
      .slice(0, TOKENS_PER_CHAIN)
      .map(t => {
        const bal = t.balance / 10 ** (t.contract_decimals || 18);
        return [
          t.contract_ticker_symbol || "—",
          t.contract_name || "Unknown",
          formatNumber(bal),
          `$${formatUSD(t.quote || 0)}`
        ];
      });

    autoTable(doc, {
      startY: y + 4,
      head: [["Symbol", "Token", "Balance", "Value (USD)"]],
      body: rows,
      theme: "striped",
      headStyles: {
        fillColor: PDF_THEME.bg,
        textColor: PDF_THEME.accent,
      },
      styles: { fontSize: 8 },
    });

    y = doc.lastAutoTable.finalY + 8;
  });

  doc.save("wallet-report.pdf");
};


  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = "/"; // go back to landing page
    } catch (err) {
      console.error("Sign-out failed:", err);
    }
  };

const PDF_THEME = {
  bg: [15, 23, 42],       // slate-900
  card: [2, 6, 23],      // slate-950
  text: [229, 231, 235], // gray-200
  muted: [148, 163, 184],
  accent: [34, 211, 238], // cyan
};


  /* ==========================================
     NAVBAR SHRINK + TOP BUTTON (only when wallet loaded)
  ========================================== */
useEffect(() => {
  if (!walletData) {
    setScrolled(false);
    setShowTopBtn(false);
    return;
  }

  let ticking = false;

  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const y = window.scrollY;

        setScrolled(y > 60);
        setShowTopBtn(y > 200);

        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener("scroll", handleScroll);
  handleScroll(); // initialize

  return () => window.removeEventListener("scroll", handleScroll);
}, [walletData]);



  /* ==========================================
     HANDLE INPUT
  ========================================== */
  const handleInputChange = (e) => setWalletAddress(e.target.value);

  /* ==========================================
     FORMATTERS
  ========================================== */
  const formatNumber = (num) => {
    return Number(num).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  const formatUSD = (num) => {
    return Number(num).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* ==========================================
     COPY ADDRESS ONLY
  ========================================== */
  const copyToClipboard = () => {
    if (!walletAddress) return;
    navigator.clipboard.writeText(walletAddress);
    setCopied("address");
    setTimeout(() => setCopied(false), 1300);
  };

  /* ==========================================
     COPY SHARE LINK (full URL)
  ========================================== */
  const copyShareLink = () => {
    if (!walletAddress) return;
    const url = `${window.location.origin}?wallet=${walletAddress}`;
    navigator.clipboard.writeText(url);
    setCopied("link");
    setTimeout(() => setCopied(false), 1300);
  };

  /* ==========================================
     LOAD WALLET IF ?wallet= IS PRESENT
  ========================================== */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramWallet = params.get("wallet");

    if (paramWallet) {
      setWalletAddress(paramWallet);
      handleViewWallet(paramWallet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const isValidEthAddress = (value) => {
  try {
    return ethers.isAddress(value);
  } catch {
    return false;
  }
};

const isLikelyENS = (value) => {
  return (
    value.endsWith(".eth") &&
    value.length > 4 &&
    !value.includes(" ")
  );
};

  /* ==========================================
     Misc
  ========================================== */
 const handleViewWallet = async (overrideAddress) => {
  // 🚫 Prevent scan until Pro status is fully loaded
  if (proLoading) {
    setInputStatus("Checking subscription status…");
    return;
  }

  const address = overrideAddress || walletAddress;
  const clean = address.trim().toLowerCase();

// Reset messages
setInputError("");
setInputStatus("");
setResolvingENS(false);

// Empty input
if (!clean) {
  setInputError("Please enter a wallet address or ENS name.");
  return;
}

// Valid ETH address
if (isValidEthAddress(clean)) {
  // ✅ valid — continue to fetch
}

// ENS name ENS detected — resolving
else if (isLikelyENS(clean)) {
  setInputStatus("");
  setResolvingENS(true);
}

// Garbage input
else {
  setInputError("Invalid Ethereum address or ENS name.");
  return;
}
    setLoading(true);
    setWalletData(null);
    setMarketData({});

    // Update URL for sharing
    window.history.replaceState({}, "", `?wallet=${address}`);

    try {
      const chains = [
        { name: "Ethereum", id: 1 },
        { name: "Polygon", id: 137 },
        { name: "Arbitrum", id: 42161 },
        { name: "BNB Chain", id: 56 },
        { name: "Optimism", id: 10 },
        { name: "Base", id: 8453 },
        { name: "Avalanche", id: 43114 },
      ];

const results = [];

for (const chain of chains) {
  const user = auth.currentUser;
const token = user ? await user.getIdToken() : null;

const res = await fetch(
  "https://scanwallet-6w66jvsixq-uc.a.run.app",
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      address,
      chainId: chain.id,
    }),
  }
);

const data = await res.json();

 if (res.status === 429) {
  if (isPro) {
    // 🔁 Pro user with stale token — retry ONCE
    const refreshedToken = await auth.currentUser.getIdToken(true);

    const retryRes = await fetch(
      "https://scanwallet-6w66jvsixq-uc.a.run.app",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshedToken}`,
        },
        body: JSON.stringify({
          address,
          chainId: chain.id,
        }),
      }
    );

    const retryData = await retryRes.json();

    if (retryRes.ok) {
      results.push({
        chain: chain.name,
        chainId: chain.id,
        items: retryData.data?.items || [],
      });
      continue;
    }
  }

  // Free user OR retry failed
  setInputError("LIMIT_REACHED");
  setLoading(false);
  return;
}

 if (!res.ok) {
    console.warn(`Chain ${chain.name} failed`, data);
    continue; // skip this chain instead of killing the scan
    }
 results.push({
    chain: chain.name,
    chainId: chain.id,
    items: data.data?.items || [],
  });
}

setWalletData(results);
// =============================
// RUN WALLET ALERTS (PERMANENT)
// =============================
const alerts = await runWalletAlerts({
  address,
  chain: "ethereum", // reference chain for pending tx logic
  allChainsData: results,
  onChainTokens: buildAllTokensFlat(results),
  visibleTokens: buildAllTokensFlat(results), // same for now
});

setWalletAlerts(alerts);


    } catch (err) {
      console.error(err);
      alert("Failed to load wallet.");
    } finally {
      setLoading(false);
    }
  };

/* ==========================================
   FETCH 30D PRICE HISTORY (CoinGecko PRO)
   - powers sparklines + 30D%
========================================== */
useEffect(() => {
  if (!walletData) return;

  const CG_KEY = process.env.REACT_APP_COINGECKO_KEY;
  if (!CG_KEY) return;

  // Map chainId -> CoinGecko platform slug (same as your other effect)
  const platformMap = {
    1: "ethereum",
    137: "polygon-pos",
    42161: "arbitrum-one",
    56: "binance-smart-chain",
    10: "optimistic-ethereum",
    8453: "base",
    43114: "avalanche",
  };

  const fetchCoinGeckoHistory = async () => {
    try {
      const tokens = [];

      walletData.forEach((chain) => {
        const platform = platformMap[chain.chainId];
        if (!platform) return;

        chain.items
          .filter((t) => t.balance > 0 && t.contract_address)
          .slice(0, TOKENS_PER_CHAIN)
          .forEach((t) => {
            const key = `${chain.chainId}-${t.contract_address}`;
            // prevent re-fetch if already cached
            if (cgHistory[key] && cgHistory[key].length > 0) return;
            tokens.push({ chainId: chain.chainId, platform, address: t.contract_address, key });
          });
      });

      if (tokens.length === 0) return;

      // Fetch sequentially to avoid rate limits (you can parallelize later)
      for (const tok of tokens) {
        try {
          // PRO endpoint supports x-cg-pro-api-key
          const url = `https://pro-api.coingecko.com/api/v3/coins/${tok.platform}/contract/${tok.address}/market_chart/?vs_currency=usd&days=30&interval=daily`;

          const res = await fetch(url, {
            headers: {
              "x-cg-pro-api-key": CG_KEY,
              "accept": "application/json",
            },
          });

          if (!res.ok) continue;

          const data = await res.json();
          // market_chart.prices -> [ [timestamp, price], ... ]
          const series = (data?.prices || [])
            .map((p) => (Array.isArray(p) ? p[1] : null))
            .filter((v) => typeof v === "number");

          if (series.length >= 3) {
            setCgHistory((prev) => ({ ...prev, [tok.key]: series }));

            const first = series[0];
            const last = series[series.length - 1];
            const change = first > 0 ? ((last - first) / first) * 100 : 0;

            setCgChange30d((prev) => ({ ...prev, [tok.key]: change }));
          }
        } catch (innerErr) {
          console.error("CG history fetch failed:", tok, innerErr);
        }
      }
    } catch (err) {
      console.error("CoinGecko history batch failed", err);
    }
  };

  fetchCoinGeckoHistory();
  // IMPORTANT: include cgHistory so we don't refetch cached keys endlessly
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [walletData, cgHistory]);

  /* ==========================================
     FETCH PRICE + MARKET CAP FROM COINGECKO
     (lightweight, no API key, only for top tokens)
  ========================================== */
  useEffect(() => {
    if (!walletData) return;

    // Map Covalent chainId -> CoinGecko "platform" slug
    const platformMap = {
      1: "ethereum",
      137: "polygon-pos",
      42161: "arbitrum-one",
      56: "binance-smart-chain",
      10: "optimistic-ethereum",
      8453: "base",
      43114: "avalanche",
    };

    const tokensToFetch = [];

    walletData.forEach((chain) => {
      const platform = platformMap[chain.chainId];
      if (!platform) return;

      chain.items
        .filter((t) => t.balance > 0 && (t.quote || 0) > 0 && t.contract_address)
        .slice(0, TOKENS_PER_CHAIN) // keep it light
        .forEach((t) => {
          const key = `${chain.chainId}-${t.contract_address}`;
          tokensToFetch.push({ key, platform, address: t.contract_address });
        });
    });

    if (tokensToFetch.length === 0) return;

    tokensToFetch.forEach(async (entry) => {
      try {
        const url = `https://pro-api.coingecko.com/api/v3/coins/${entry.platform}/contract/${entry.address}`;
        const res = await fetch(url);
        if (!res.ok) return;

        const data = await res.json();
        const price = data?.market_data?.current_price?.usd ?? null;
        const mcap = data?.market_data?.market_cap?.usd ?? null;

        if (price != null || mcap != null) {
          setMarketData((prev) => ({
            ...prev,
            [entry.key]: {
              price,
              marketCap: mcap,
            },
          }));
        }
      } catch (err) {
        console.error("CoinGecko fetch failed for", entry, err);
      }
    });
  }, [walletData]);

// Pro Status Updates
useEffect(() => {
  const loadProStatus = async () => {
    const user = auth.currentUser;

    if (!user) {
      setIsPro(false);
      setProLoading(false);
      return;
    }

    try {
      // 🔄 Force refresh token to get latest Stripe claims
      const tokenResult = await user.getIdTokenResult(true);
      setIsPro(tokenResult.claims?.pro === true);
    } catch (err) {
      console.error("Failed to load Pro status", err);
      setIsPro(false);
    } finally {
      setProLoading(false);
    }
  };

  loadProStatus();
}, []);


  /* ==========================================
     TOTALS
  ========================================== */
  const getChainTotal = (items) =>
    items
      .filter((t) => t.balance > 0)
      .reduce((sum, t) => sum + (t.quote || 0), 0);

  const getPortfolioTotal = () => {
    if (!walletData) return 0;
    return walletData.reduce((sum, chain) => sum + getChainTotal(chain.items), 0);
  };

  /* ==========================================
     PORTFOLIO DISTRIBUTION (PER TOKEN)
  ========================================== */
  const getPortfolioSlices = () => {
    if (!walletData) return [];

    const tokenMap = new Map();

    walletData.forEach((chain) => {
      chain.items
        .filter((t) => t.balance > 0 && t.quote > 0)
        .forEach((t) => {
          const label = t.contract_ticker_symbol || t.contract_name || "Unknown";
          const key = label;
          const value = t.quote || 0;

          const existing = tokenMap.get(key) || { label, value: 0 };
          existing.value += value;
          tokenMap.set(key, existing);
        });
    });

    const all = Array.from(tokenMap.values()).sort((a, b) => b.value - a.value);
    if (all.length === 0) return [];

    const top = all.slice(0, PIE_TOP_TOKENS);
    const others = all.slice(PIE_TOP_TOKENS);
    const othersTotal = others.reduce((sum, t) => sum + t.value, 0);

    let slices = top;
    if (othersTotal > 0) {
      slices = [...top, { label: "Other", value: othersTotal }];
    }

    const total = slices.reduce((sum, s) => sum + s.value, 0) || 1;

    const colors = ["#00FFE7", "#00A8FF", "#FFB020", "#FF5C93", "#9B6BFF", "#4ADE80"];

    return slices.map((s, idx) => ({
      ...s,
      percent: (s.value / total) * 100,
      color: colors[idx % colors.length],
    }));
  };

  const portfolioSlices = getPortfolioSlices();

  const buildPieGradient = (slices) => {
    if (!slices || slices.length === 0) {
      return "conic-gradient(#1f2937 0deg 360deg)";
    }
    let currentAngle = 0;
    const parts = slices.map((slice) => {
      const start = currentAngle;
      const end = start + (slice.percent / 100) * 360;
      currentAngle = end;
      return `${slice.color} ${start}deg ${end}deg`;
    });
    return `conic-gradient(${parts.join(", ")})`;
  };

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function drawPieChartCanvas(slices, size = 220) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  const center = size / 2;
  const radius = size / 2 - 10;

  let startAngle = 0;

  slices.forEach((slice) => {
    const angle = (slice.percent / 100) * Math.PI * 2;

    ctx.beginPath();
    ctx.moveTo(center, center);
    ctx.arc(center, center, radius, startAngle, startAngle + angle);
    ctx.closePath();
    ctx.fillStyle = slice.color;
    ctx.fill();

    startAngle += angle;
  });

  return canvas.toDataURL("image/png");
}


  /* ==========================================
     AI ENGINE — Top 5 Tokens with Unit Price & Market Cap
  ========================================== */
  const buildTokenRecommendations = () => {
    if (!walletData) return [];

    const totalPortfolio = getPortfolioTotal() || 1;
    const tokenMap = new Map();

    walletData.forEach((chain) => {
      const chainId = chain.chainId;
      const chainName = chain.chain;

      chain.items
        .filter((t) => t.balance > 0 && (t.quote || 0) > 0)
        .forEach((t) => {
          const key =
            (chainId || "x") +
            "-" +
            (t.contract_address || t.contract_ticker_symbol || t.contract_name);

          const balance = t.balance / 10 ** (t.contract_decimals || 18);
          const fallbackUnitPrice =
            balance > 0 ? (t.quote || 0) / balance : null;

          const marketKey =
            chainId && t.contract_address
              ? `${chainId}-${t.contract_address}`
              : null;

          const cgEntry = marketKey ? marketData[marketKey] : null;
          const cgPrice = cgEntry?.price ?? null;
          const cgMarketCap = cgEntry?.marketCap ?? null;

          const existing =
            tokenMap.get(key) || {
              label: t.contract_name || t.contract_ticker_symbol || "Unknown",
              symbol: t.contract_ticker_symbol || "",
              chains: new Set(),
              totalValueUSD: 0,
              historySeries: null,
              pricePerToken: null,
              marketCapUSD: null,
              chainId: chainId || null,
              address: t.contract_address || null,

            };

          existing.totalValueUSD += t.quote || 0;
          existing.chains.add(chainName);

          // Prefer CoinGecko price; fallback to implied price from wallet
          if (cgPrice != null) {
            existing.pricePerToken = cgPrice;
          } else if (fallbackUnitPrice != null && existing.pricePerToken == null) {
            existing.pricePerToken = fallbackUnitPrice;
          }

          if (cgMarketCap != null) {
            existing.marketCapUSD = cgMarketCap;
          }

          if (chainId && t.contract_address) {
            const historyKey = `${chainId}-${t.contract_address}`;
            if (!existing.historySeries && cgHistory[historyKey]) {
              existing.historySeries = cgHistory[historyKey];
            }
          }

          tokenMap.set(key, existing);
        });
    });

    const recs = Array.from(tokenMap.values()).map((tok) => {
      const allocationPct = (tok.totalValueUSD / totalPortfolio) * 100;
      
      let changePct = null;
      let volatility = 0;

      // If we have a direct 30D% from CoinGecko, use it
      if (tok.chainId && tok.address) {
        const k = `${tok.chainId}-${tok.address}`;
        if (cgChange30d[k] !== undefined && cgChange30d[k] !== null) {
          changePct = cgChange30d[k];
      }
    }

      // Volatility still uses series if available
      if (tok.historySeries && tok.historySeries.length >= 2) {
        const series = tok.historySeries;
        const mean = series.reduce((sum, v) => sum + v, 0) / series.length;

        if (mean > 0) {
          const variance =
            series.reduce((sum, v) => sum + (v - mean) ** 2, 0) / series.length;
          volatility = (Math.sqrt(variance) / mean) * 100;
        }
      }
        
      const clampedGrowth = Math.max(-50, Math.min(50, changePct));
      const growthScore = ((clampedGrowth + 50) / 100) * 40;
      const allocScore = Math.min(allocationPct, 25);

      const volClamped = Math.min(volatility || 0, 60);
      const stabilityScore = Math.max(0, 30 - volClamped);

      const diversityScore = tok.chains.size > 1 ? 5 : 0;

      const score = growthScore + allocScore + stabilityScore + diversityScore;

      const reasons = [];

      if (changePct > 5) {
        reasons.push(`Strong 30-day growth of ${changePct.toFixed(1)}%`);
      } else if (changePct > 0) {
        reasons.push(`Positive 30-day trend (+${changePct.toFixed(1)}%)`);
      } else if (changePct < -5) {
        reasons.push(`Dipped ${changePct.toFixed(1)}% — may be undervalued`);
      }

      if (volatility && volatility < 15)
        reasons.push("Stable low volatility");
      else if (volatility && volatility > 35)
        reasons.push("High volatility — higher risk");

      reasons.push(`Represents ${allocationPct.toFixed(1)}% of this portfolio`);

      if (tok.chains.size > 1) {
        reasons.push("Held on multiple chains");
      }

      return {
        ...tok,
        chains: Array.from(tok.chains),
        allocationPct,
        changePct,
        volatility,
        score,
        reasons,
      };
    });

    recs.sort((a, b) => b.score - a.score);
    return recs.slice(0, AI_TOP_TOKENS);
  };


  const recommendations = walletData ? buildTokenRecommendations() : [];
/* ============================================================
   INTELLIGENCE ENGINE — CORE SCORING FUNCTIONS (Phase 2)
   ============================================================ */

/* 1️⃣ Wallet Health Score (0–100) */
function calculateWalletHealthScore(allTokens) {
  if (!allTokens || allTokens.length === 0) return 0;

  let score = 70; // baseline neutral score

  const stablecoins = ["usdt", "usdc", "dai"];
  const totalValue = allTokens.reduce((s, t) => s + (t.valueUSD || 0), 0) || 1;

  const stables = allTokens.filter((t) =>
    stablecoins.includes(t.symbol?.toLowerCase())
  );

  const stableValue = stables.reduce((s, t) => s + (t.valueUSD || 0), 0);
  const stablePct = stableValue / totalValue;

  // Stablecoins 20–40% → healthy
  if (stablePct > 0.2 && stablePct < 0.4) score += 10;
  if (stablePct < 0.05) score -= 10; // too risky
  if (stablePct > 0.6) score -= 10; // too defensive

  // Count tiny "dust" tokens
  const dust = allTokens.filter((t) => (t.valueUSD || 0) < 1).length;
  if (dust > 5) score -= 10;

  // Chain diversification
  const chains = new Set(allTokens.map((t) => t.chainId));
  if (chains.size >= 3) score += 5;

  return Math.min(100, Math.max(0, Math.round(score)));
}

/* 2️⃣ Token Risk Levels (Low / Medium / High / Speculative) */
function calculateTokenRiskLevels(tokens) {
  return tokens.map((t) => {
    const usd = t.valueUSD || 0;
    let risk = "Medium";

    if (usd < 1) risk = "High";
    if (usd < 0.1) risk = "Speculative";
    if (usd > 500) risk = "Low";

    return { ...t, risk };
  });
}

/* 3️⃣ Token Clutter Score (0–100 messy) */
function calculateTokenClutter(tokens) {
  const dustCount = tokens.filter((t) => (t.valueUSD || 0) < 0.01).length;
  return Math.min(100, dustCount * 6); // 6 pts per dust token
}

/* 4️⃣ Chain Dominance (% of total value per chain) */
function calculateChainDominance(chainTotals) {
  const total = Object.values(chainTotals).reduce((s, v) => s + v, 0) || 1;
  const dominance = {};

  for (const [chain, val] of Object.entries(chainTotals)) {
    dominance[chain] = ((val / total) * 100).toFixed(1);
  }

  return dominance;
}

/* 5️⃣ Affordable Token Suggestions (< $20, high-ranking MC) */
function getAffordableTokenSuggestions(walletData, marketData) {
  if (!walletData) return [];

  const allTokens = buildAllTokensFlat(walletData);

  // Attach CoinGecko pricing info
  const merged = allTokens.map((t) => {
    const key = `${t.chainId}-${t.address}`;
    const cg = marketData[key] || {};

    return {
      symbol: t.symbol || "?",
      label: t.name || "Unknown",
      price: cg.price ?? null,
      marketCap: cg.marketCap ?? null,
    };
  });

  // Filter & sort
  return merged
    .filter((t) => t.price != null && t.price < 20 && t.marketCap)
    .sort((a, b) => b.marketCap - a.marketCap)
  .slice(0, AFFORDABLE_TOP_TOKENS);

}


/* ============================================================
   BUILD FLAT TOKEN LIST — USED BY ALL INTELLIGENCE FEATURES
   ============================================================ */

function buildAllTokensFlat(walletData) {
  if (!walletData) return [];

  const list = [];

  walletData.forEach((chain) => {
    const chainName = chain.chain;
    const chainId = chain.chainId;

    chain.items
      .filter((t) => t.balance > 0)
      .forEach((t) => {
        const balance = t.balance / 10 ** (t.contract_decimals || 18);
        const valueUSD = t.quote || 0;

        list.push({
          chain: chainName,
          chainId,
          symbol: t.contract_ticker_symbol || "",
          name: t.contract_name || "",
          valueUSD,
          balance,
          address: t.contract_address,
        });
      });
  });

  return list;
}

/* ============================================================
   INTELLIGENCE ENGINE — COMPUTE 5 METRICS
   ============================================================ */

const allTokens = walletData ? buildAllTokensFlat(walletData) : [];

const walletHealth = walletData
  ? calculateWalletHealthScore(allTokens)
  : 0;

const tokenRisks = walletData
  ? calculateTokenRiskLevels(allTokens)
  : [];

const clutterScore = walletData
  ? calculateTokenClutter(allTokens)
  : 0;

const improvementTips = walletData
  ? buildWalletImprovementTips(allTokens, walletHealth, clutterScore)
  : [];

const dustInfo = walletData
  ? buildDustCleanup(allTokens)
  : { count: 0, value: 0 };

const affordableSuggestions = walletData
  ? getAffordableTokenSuggestions(walletData, marketData)
  : [];

// =============================
// SCORE INTERPRETATION HELPERS
// =============================
function interpretWalletHealth(score) {
  if (score >= 80) return { label: "Very Strong Wallet", icon: "🟦", color: "#00eaff" };
  if (score >= 60) return { label: "Healthy Wallet", icon: "✅", color: "#00ffa6" };
  if (score >= 30) return { label: "Needs Improvement", icon: "⚠️", color: "#ffcc00" };
  return { label: "Risky Wallet", icon: "🚨", color: "#ff5c5c" };
}

function interpretClutter(score) {
  if (score <= 20) return { label: "Clean Wallet", icon: "🧼", color: "#00ffa6" };
  if (score <= 50) return { label: "Mild Clutter", icon: "🙂", color: "#00eaff" };
  if (score <= 80) return { label: "Cluttered", icon: "😬", color: "#ffcc00" };
  return { label: "Highly Cluttered", icon: "🚮", color: "#ff5c5c" };
}
/* ============================================================
   NEW: Dust Cleanup Summary
   ============================================================ */
function buildDustCleanup(allTokens) {
  if (!allTokens) return { count: 0, value: 0 };

  const dust = allTokens.filter(t => (t.valueUSD || 0) < 0.01);
  const totalDustValue = dust.reduce((s, t) => s + (t.valueUSD || 0), 0);

  return {
    count: dust.length,
    value: totalDustValue
  };
}

/* ============================================================
   NEW: Improvement Tips Engine
   ============================================================ */
function buildWalletImprovementTips(allTokens, walletHealth, clutterScore) {
  if (!allTokens || allTokens.length === 0) return [];

  const tips = [];

  const total = allTokens.reduce((s, t) => s + (t.valueUSD || 0), 0) || 1;
  const stables = allTokens.filter((t) =>
    ["usdt", "usdc", "dai"].includes(t.symbol?.toLowerCase())
  );
  const stablePct = stables.reduce((s, t) => s + (t.valueUSD || 0), 0) / total;

  const chains = new Set(allTokens.map((t) => t.chainId));

  const dustTokens = allTokens.filter((t) => (t.valueUSD || 0) < 0.01);
  const lowCapTokens = allTokens.filter((t) => (t.valueUSD || 0) < 1);

  // --- Health-based advice ---
  if (walletHealth < 40) {
    tips.push("Wallet shows high risk — consider reducing exposure to volatile assets.");
  }
  if (walletHealth < 60) {
    tips.push("Improve diversification by spreading value across more tokens.");
  }
  if (stablePct < 0.05) {
    tips.push("Holding almost no stablecoins — consider adding USDC/USDT/DAI for stability.");
  }
  if (stablePct > 0.6) {
    tips.push("Large stablecoin position — consider reallocating to growth assets.");
  }

  // --- Chain diversification ---
  if (chains.size < 2) {
    tips.push("Wallet is concentrated on one chain — add assets on Polygon, Arbitrum, etc.");
  }

  // --- Dust/clutter related ---
  if (dustTokens.length > 10) {
    tips.push(`You have ${dustTokens.length} micro-dust tokens — consider cleanup.`);
  }
  if (clutterScore > 70) {
    tips.push("High clutter score — removing abandoned tokens can improve wallet clarity.");
  }

  return tips.slice(0, 5); // Show up to 5
}

  const renderUpgradeNotice = () => {
  if (inputError !== "LIMIT_REACHED") return null;

  return (
  <div className="upgrade-overlay">
    <div className="upgrade-notice fade-in">

      <p className="upgrade-title">
        Upgrade to Pro — $19 CAD / year
      </p>

      <p className="upgrade-subtitle">
        Billed annually. Cancel anytime.
        <br />
        Upgrade to <strong>Pro</strong> for high-volume daily scanning,
        <strong> full PDF reports</strong>, and advanced insights.
      </p>

      <button
        className="upgrade-btn"
        onClick={handleUpgrade}
        disabled={checkoutLoading}
      >
        {checkoutLoading ? "Redirecting to secure checkout…" : "Upgrade to Pro"}
      </button>

      <button
        className="upgrade-close"
        onClick={() => setInputError("")}
      >
        Cancel
      </button>
    </div>
  </div>
  );
};

  return (
    <div className="app-container">
      {/* NEON BACKGROUND */}
      <div className="background-particles" />

      {/* LOADING OVERLAY */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner-ring" />
            <img src="/badger.png" alt="loading" />
            <p>Scanning chains...</p>
          </div>
        </div>
      )}

      {/* STRIPE CHECKOUT REDIRECT OVERLAY */}
      {checkoutLoading && (
         <div className="checkout-overlay fade-in">
           <div className="checkout-box">
             <div className="spinner-ring" />
             <p>Redirecting to secure Stripe checkout…</p>
             <p className="checkout-sub">Please do not refresh</p>
           </div>
          </div>
      )}

      {/* NAVBAR */}
      <header id="navbar" className={`navbar ${scrolled ? "navbar-shrunk" : ""}`}>
        <div className="logo-wrapper">
          <span className="logo-scanline" />
          <img src="/badger.png" className="badger-logo" alt="logo" />
        </div>

        <div className="nav-titles">
          <h1 className="nav-title-main">MultiVersal Blockchain Tracer</h1>

          <div className="nav-sub-row">
            <span className="version-inline">The Wallet Finder</span>
            <span className="version-inline">v3.0.3</span>
          </div>
        </div>


  <div className="nav-auth">
    {auth.currentUser && (
      <>
        <span className="nav-email">{auth.currentUser.email}</span>
        <button className="signout-btn" onClick={handleSignOut}>
          Refresh
        </button>
      </>
    )}
  </div>
      </header>

      {/* MAIN CONTENT */}
      <main>
	{renderUpgradeNotice()}

        {!walletData ? (
          <div className="center-screen fade-in-up">
            <div className="glass-card">
              <h2>Enter Wallet Address</h2>
              <p className="subtitle">
                Scan balances across Ethereum, Polygon, Arbitrum, BNB, Optimism, Base & Avalanche.
              </p>

              <div className="input-row">
                <input
                  type="text"
                  placeholder="0x1234... or ENS name"
                  value={walletAddress}
                  onChange={handleInputChange}
                />
                <button onClick={() => handleViewWallet()} disabled={loading}>
                  {loading ? "Loading..." : "View Wallet"}
                </button>
              </div>
<div className="wallet-trust-links">
  <a
    href="/about"
    className="trust-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    About & Transparency
  </a>

  <span className="trust-separator">·</span>

  <a
    href="/privacy"
    className="trust-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Privacy Policy
  </a>

  <span className="trust-separator">·</span>

  <a
    href="/terms"
    className="trust-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Terms of Use
  </a>
</div>
                 {/* Upgrade flow */}
{inputError === "LIMIT_REACHED" && (
  <div className="upgrade-notice fade-in">
    <p className="upgrade-title">
      You’ve reached today’s free scan limit. 1 scan per day.
    </p>

    <p className="upgrade-subtitle">
       Upgrade to <strong>Pro - $19 CAD / per year</strong> Unlock high‑volume daily scanning, full PDF reports, and               advanced insights with Pro.
    </p>

    <button
      className="upgrade-btn"
      onClick={handleUpgrade}
      disabled={checkoutLoading}
    >
      {checkoutLoading ? "Redirecting to Stripe…" : "Upgrade to Pro"}
    </button>
  </div>
)}

{/* Normal validation errors */}
{inputError && inputError !== "LIMIT_REACHED" && (
  <p className="input-error-msg fade-in">
    {inputError}
  </p>
)}

                  {inputStatus && (
  <p className="input-status-msg fade-in">{inputStatus}</p>
)}

            </div>
          </div>
        ) : (
          <div className="results-container fade-in-up">
            <div className="glass-card">
              <button className="top-back-btn" onClick={() => setWalletData(null)}>
                ← Back
              </button>

              <h2>Wallet Overview</h2>
              <button
              className={`pdf-btn ${!isPro ? "pdf-locked" : ""}`}
              onClick={handlePdfClick}
             >
              📄 Download Wallet Report (PDF)
             </button>


               {!isPro && (
               <p className="pdf-lock-note">
               PDF reports are available on the Pro plan.
            </p>
          )}

             {/* ADDRESS + COPY + SHARE */}
              <div className="wallet-header">
                <span className="address">{walletAddress}</span>

                <span className="copy-btn" onClick={copyToClipboard}>
                  📄
                </span>
                <span className="share-btn" onClick={copyShareLink}>
                  🔗
                </span>

                {copied === "address" && (
                  <span className="copied-popup">Address Copied!</span>
                )}
                {copied === "link" && (
                  <span className="copied-popup">Share Link Copied!</span>
                )}
              </div>

              {/* TOTAL PORTFOLIO VALUE */}
              <h2 style={{ marginTop: "10px", marginBottom: "15px" }}>
                Total Portfolio Value: ${formatUSD(getPortfolioTotal())}
              </h2>
	      {/* WALLET ALERTS */}
              <WalletAlertsPanel alerts={walletAlerts} />

              {/* PORTFOLIO PIE CHART */}
              {portfolioSlices.length > 0 && (
                <div className="portfolio-section">
                  <h3>Portfolio Distribution</h3>
                  <div className="pie-layout">
                    <div
                      className="pie-chart"
                      style={{ backgroundImage: buildPieGradient(portfolioSlices) }}
                    />
                    <ul className="pie-legend">
                      {portfolioSlices.map((slice) => (
                        <li key={slice.label}>
                          <span
                            className="legend-color"
                            style={{ backgroundColor: slice.color }}
                          />
                          <span className="legend-label">{slice.label}</span>
                          <span className="legend-value">
                            {slice.percent.toFixed(1)}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
)}

{/* ============================================================
    INTELLIGENCE PANEL — 5 Killer Metrics
   ============================================================ */}

<div className="intelligence-section glass-card">

  {/* ⭐ ROW: Health + Clutter */}
  <div className="intel-row">

    {/* 1️⃣ Wallet Health Score */}
    <div className="intel-box">
      <h3>Wallet Health Score</h3>

      {(() => {
        const info = interpretWalletHealth(walletHealth ?? 0);
        return (
          <>
            <p className="intel-score" style={{ color: info.color }}>
              {info.icon} {walletHealth}/100
            </p>
            <p className="intel-label">{info.label}</p>
            <p className="intel-note">
              Based on diversification, stability & risk exposure.
            </p>
          </>
        );
      })()}
    </div>

<div className="intel-wide improvement-box">
  <h3>Wallet Improvement Suggestions</h3>

  {improvementTips.length > 0 ? (
    <ul className="tips-list">
      {improvementTips.map((tip, i) => (
        <li key={i} className="tip-item">• {tip}</li>
      ))}
    </ul>
  ) : (
    <p className="intel-note">
      Your wallet looks well-balanced — no major improvements needed.
    </p>
  )}
</div>



    {/* 2️⃣ Token Clutter Score */}
    <div className="intel-box">
      <h3>Token Clutter Score</h3>

      {(() => {
        const info = interpretClutter(clutterScore ?? 0);
        return (
          <>
            <p className="intel-score" style={{ color: info.color }}>
              {info.icon} {clutterScore}
            </p>
            <p className="intel-label">{info.label}</p>
            <p className="intel-note">
              Measures low-value, dust, or abandoned tokens.
            </p>
          </>
        );
      })()}
    </div>
<div className="intel-wide dust-box">
  <h3>Dust Cleanup Advisor</h3>

  {dustInfo.count > 0 ? (
    <>
      <p className="intel-label">You have {dustInfo.count} dust tokens</p>
      <p className="intel-note">Total dust value: ${formatUSD(dustInfo.value)}</p>

      <ul className="tips-list">
        <li className="tip-item">• Hide or archive dust tokens</li>
        <li className="tip-item">• Swap dust for gas (if liquidity exists)</li>
        <li className="tip-item">• Remove abandoned or spam tokens</li>
      </ul>
    </>
  ) : (
    <p className="intel-note">No dust detected — wallet is clean.</p>
  )}
</div>

        {/* 3️⃣ Top Affordable Tokens */}
        <div className="intel-box">
            <h3>Value Insights: Tokens Worth Watching</h3>
      <h4>Strategic Picks for You</h4>
<p className="intel-subtitle">
  Top tokens under $20 selected by market cap strength and price stability.
</p>

            {affordableSuggestions && affordableSuggestions.length > 0 ? (
                <ul className="affordable-grid">
  {affordableSuggestions.map((t, i) => (
    <li key={i} className="affordable-card">
      
      <div className="aff-left">
        <div className="aff-symbol">{t.symbol}</div>
        <div className="aff-label">{t.label}</div>
      </div>

      <div className="aff-right">
        <div className="aff-price">${formatUSD(t.price)}</div>
        <div className="aff-mcap">MC: {(t.marketCap / 1e9).toFixed(2)}B</div>
      </div>

    </li>
  ))}
</ul>

            ) : (
                <p className="ai-subtitle">
  No affordable picks available — try scanning a wallet with mid-cap or lower-priced tokens.
</p>

            )}
        </div>
    </div>


    {/* 4️⃣ Chain Dominance section deleted */}
 

    {/* 5️⃣ Token Risk Table */}
    <div className="intel-wide">
        <h3>Token Risk Scanner</h3>
        <table className="risk-table">
            <thead>
                <tr>
                    <th>Token</th>
                    <th>Value</th>
                    <th>Risk</th>
                </tr>
            </thead>
            <tbody>
                {tokenRisks &&
                    tokenRisks.slice(0, RISK_TABLE_TOKENS).map((t, i) => (
                        <tr key={i}>
                            <td>{t.symbol || t.name}</td>
                            <td>${formatUSD(t.valueUSD)}</td>
                            <td className={`risk-${t.risk.toLowerCase()}`}>{t.risk}</td>
                        </tr>
                    ))}
            </tbody>
        </table>
    </div>
</div>

              {/* AI RECOMMENDATION SECTION */}
              {recommendations.length > 0 && (
                <div className="ai-section">
                  <h3>AI Token Recommendations</h3>
                  <p className="ai-subtitle">
                    Your top tokens based on 30-day performance and stability.
                  </p>

                  <div className="ai-token-grid">
                    {recommendations.map((tok, index) => (
                      <div className="ai-token-card" key={tok.label + index}>
                        <div className="ai-token-header">
                          <span className="ai-rank">#{index + 1}</span>
                          <div className="ai-token-names">
                            <span className="ai-token-symbol">
                              {tok.symbol || "—"}
                            </span>
                            <span className="ai-token-label">{tok.label}</span>
                          </div>
                          <span className="ai-score">
                            {tok.score.toFixed(0)}/100
                          </span>
                        </div>

                        <div className="ai-token-metrics">
                          <span>
                            30D:{" "}
                            {tok.changePct !== null &&
                            tok.changePct !== undefined
                              ? `${tok.changePct > 0 ? "+" : ""}${tok.changePct.toFixed(1)}%`
                              : "n/a"}
                          </span>

                          <span>Alloc: {tok.allocationPct.toFixed(1)}%</span>

                          <span>
                            Unit Price:{" "}
                            {tok.pricePerToken
                              ? `$${formatUSD(tok.pricePerToken)}`
                              : "n/a"}
                          </span>

                          <span>
                            MC:{" "}
                            {tok.marketCapUSD
                              ? `$${(tok.marketCapUSD / 1e9).toFixed(2)}B`
                              : "Not indexed"}
                          </span>
                        </div>

                        <ul className="ai-reasons">
                          {tok.reasons.slice(0, 3).map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <p className="ai-disclaimer">
                    Not financial advice — these insights are generated automatically.
                  </p>
                </div>
              )}

              {/* PER-CHAIN TABLES */}
              {walletData.map((chain) => {
                const chainTotal = getChainTotal(chain.items);
                return (
                  <div key={chain.chain}>
                    <h3>{chain.chain}</h3>

                    <p
                      style={{
                        marginTop: "-5px",
                        marginBottom: "10px",
                        fontWeight: "600",
                        color: "#a0eaff",
                      }}
                    >
                      Chain Total: ${formatUSD(chainTotal)}
                    </p>

                    <table>
                      <thead>
                        <tr>
                          <th>Token</th>
                          <th>Balance</th>
                          <th>Value (USD)</th>
                        </tr>
                      </thead>

                      <tbody>
                        {chain.items
                          .filter((t) => t.balance > 0)
                          .slice(0, TOKENS_PER_CHAIN)
                          .map((t) => {
                            const balance =
                              t.balance / 10 ** t.contract_decimals;
                            const valueUSD = t.quote || 0;
                            const historyKey =
                              chain.chainId && t.contract_address
                                ? `${chain.chainId}-${t.contract_address}`
                                : null;

                            const realSeries = historyKey ? cgHistory[historyKey] : null;

                            const sparkPoints =
                              realSeries && realSeries.length >= 3
                                ? realSeries
                                : buildSyntheticSeries(valueUSD);

                            return (
                              <tr key={t.contract_address}>
                                <td className="token-cell">
                                  <img
                                    src={t.logo_url}
                                    className="token-logo"
                                    alt={t.contract_ticker_symbol}
                                    onError={(e) =>
                                      (e.target.src =
                                        "https://via.placeholder.com/32?text=?")
                                    }
                                  />
                                  <div className="token-info">
                                    <strong>
                                      {t.contract_name || "Unknown"}
                                    </strong>
                                    <span className="symbol">
                                      {t.contract_ticker_symbol}
                                    </span>
                                  </div>
                                </td>

                                <td>{formatNumber(balance)}</td>

                                <td>
                                  <div className="value-with-spark">
                                    <span className="value-text">
                                      ${formatUSD(valueUSD)}
                                    </span>
                                    <Sparkline points={sparkPoints} />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              <button onClick={() => setWalletData(null)}>Back</button>
            </div>
          </div>
        )}
      </main>

      {walletData && showTopBtn && (
        <button
          className="back-to-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          ↑ Top
        </button>
      )}

      {/* FOOTER */}
      <footer className="footer">
        <p>Built in Canada 🍁 by Erling Garcia &amp; Son | Powered by Covalent API</p>
        <p>“Transparency is our Foundation — Built for Trust.”</p>
      </footer>
    </div>
  );
}

export default WalletTracer;